import type { RuleStepStatus } from '../EventsTab/RuleStep';
import type { PlanLibraryPlan } from 'hooks/planLibrary/planLibraryHooks';

export type PlanLibraryStepId =
  | 'name'
  | 'tasks'
  | 'forms'
  | 'documents'
  | 'roster'
  | 'hwcg'
  | 'layers'
  | 'incidentTags';

export type PlanLibraryStepDef = {
  id: PlanLibraryStepId;
  title: string;
};

const BASE_STEPS: PlanLibraryStepDef[] = [
  { id: 'name', title: 'Name & Description' },
  { id: 'tasks', title: 'Template Tasks' },
  { id: 'forms', title: 'ICS Forms' },
  { id: 'documents', title: 'Documents' },
  { id: 'roster', title: 'Roster Template' },
  { id: 'layers', title: 'Map layers' },
  { id: 'incidentTags', title: 'Use in case of incident' },
];

const HWCG_STEP: PlanLibraryStepDef = {
  id: 'hwcg',
  title: 'HWCG create incident',
};

export type PlanRosterAttachment = {
  rosterTemplateIds: string[];
  filledRosterTemplateId: string | null;
};

/**
 * A plan holds one roster template. Checking one replaces whatever is attached.
 * Unchecking removes only that row so a plan that already has several keeps the
 * rest until the user picks a replacement.
 */
export function selectPlanRosterTemplate(
  current: PlanRosterAttachment,
  row: { kind: 'empty' | 'filled'; id: string },
  checked: boolean
): PlanRosterAttachment {
  if (checked) {
    return row.kind === 'empty'
      ? { rosterTemplateIds: [row.id], filledRosterTemplateId: null }
      : { rosterTemplateIds: [], filledRosterTemplateId: row.id };
  }
  if (row.kind === 'filled') {
    return {
      rosterTemplateIds: current.rosterTemplateIds,
      filledRosterTemplateId:
        current.filledRosterTemplateId === row.id
          ? null
          : current.filledRosterTemplateId,
    };
  }
  return {
    rosterTemplateIds: current.rosterTemplateIds.filter((id) => id !== row.id),
    filledRosterTemplateId: current.filledRosterTemplateId,
  };
}

/**
 * Ordered steps. When HWCG is on, Roster Template sits immediately under Name &
 * Description, then the rest of the content steps, then HWCG create incident
 * before layers / incident tags.
 */
export function planLibraryStepList(hwcg: boolean): PlanLibraryStepDef[] {
  if (!hwcg) return BASE_STEPS;
  const withoutRoster = BASE_STEPS.filter((step) => step.id !== 'roster');
  const nameIndex = withoutRoster.findIndex((step) => step.id === 'name');
  const beforeLayers = withoutRoster.filter(
    (step) => step.id !== 'layers' && step.id !== 'incidentTags'
  );
  const layersAndTags = withoutRoster.filter(
    (step) => step.id === 'layers' || step.id === 'incidentTags'
  );
  const rosterStep = BASE_STEPS.find((step) => step.id === 'roster');
  if (!rosterStep || nameIndex < 0) return BASE_STEPS;
  return [
    ...beforeLayers.slice(0, nameIndex + 1),
    rosterStep,
    ...beforeLayers.slice(nameIndex + 1),
    HWCG_STEP,
    ...layersAndTags,
  ];
}

export type PlanLibraryStepState = {
  summary: string;
  status: RuleStepStatus;
};

function countLabel(count: number, singular: string, plural?: string): string {
  if (count === 0) return 'None';
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${word}`;
}

export function planLibraryStepStates(
  plan: PlanLibraryPlan
): Record<PlanLibraryStepId, PlanLibraryStepState> {
  const name = plan.name.trim();
  const description = plan.description?.trim() ?? '';
  const tasks = plan.tasks?.length ?? 0;
  const forms = plan.forms?.length ?? 0;
  const files = plan.files?.length ?? 0;
  const roster = plan.rosterTemplateIds?.length ?? 0;
  const layers = plan.layers?.length ?? 0;
  const rp = plan.responsiblePartyCompany?.trim() ?? '';
  const filled = Boolean(plan.filledRosterTemplateId);
  const useFor = plan.useForIncidents;
  const categories = plan.incidentCategories?.length ?? 0;
  const aors = plan.aorIds?.length ?? 0;

  let rosterSummary = 'Not set';
  if (roster > 0 && filled) {
    rosterSummary = `${countLabel(roster, 'empty template', 'empty templates')} · 1 filled`;
  } else if (roster > 0) {
    rosterSummary = countLabel(roster, 'empty template', 'empty templates');
  } else if (filled) {
    rosterSummary = '1 filled';
  }

  let incidentSummary = 'Not set';
  if (useFor) {
    const parts: string[] = [];
    if (categories > 0)
      parts.push(countLabel(categories, 'category', 'categories'));
    if (aors > 0) parts.push(countLabel(aors, 'AOR'));
    incidentSummary = parts.length > 0 ? parts.join(' · ') : 'Enabled';
  }

  return {
    name: {
      summary: name
        ? description
          ? `${name} — ${description.slice(0, 40)}${description.length > 40 ? '…' : ''}`
          : name
        : 'Not set',
      status: name ? 'complete' : 'incomplete',
    },
    tasks: {
      summary: countLabel(tasks, 'task'),
      status: tasks > 0 ? 'complete' : 'incomplete',
    },
    forms: {
      summary: countLabel(forms, 'form'),
      status: forms > 0 ? 'complete' : 'incomplete',
    },
    documents: {
      summary: countLabel(files, 'document'),
      status: files > 0 ? 'complete' : 'incomplete',
    },
    roster: {
      summary: rosterSummary,
      status: roster > 0 || filled ? 'complete' : 'incomplete',
    },
    hwcg: {
      summary: rp || 'Not set',
      status: rp ? 'complete' : 'incomplete',
    },
    layers: {
      summary: countLabel(layers, 'layer'),
      status: layers > 0 ? 'complete' : 'incomplete',
    },
    incidentTags: {
      summary: incidentSummary,
      status: useFor ? 'complete' : 'incomplete',
    },
  };
}
